// src/pages/Contact.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import BASE_URL from "../config";
import { localePath } from "../utils/content";
import Seo from "../components/Seo";

export default function Contact() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState({ loading: false, success: "", error: "" });

  const change = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: "", error: "" });
    try {
      await axios.post(`${BASE_URL}/api/contact_dajc`, {
        ...form,
        // Tag the source so backend can route to the DAJC mailbox
        direction: "DAJC",
        target_email: t("contact.email_site"),
      });
      setStatus({ loading: false, success: t("contact.success"), error: "" });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      setStatus({ loading: false, success: "", error: t("contact.error") });
    }
  };

  return (
    <>
      <Seo
        path="contact"
        title={t("contact.title")}
        description={t("contact.subtitle")}
        breadcrumb={[
          { name: t("common.home"), path: "" },
          { name: t("menu.contact"), path: "contact" },
        ]}
      />

      <section className="page-header">
        <div className="container">
          <div className="page-header__inner">
            <div className="page-header__crumbs">
              <Link to={localePath(lang)}>{t("common.home")}</Link>
              <span>/</span>
              <span>{t("menu.contact")}</span>
          
            </div>
            <h1>{t("contact.title")}</h1>
            <p>{t("contact.subtitle")}</p>
          </div>
        </div>
      </section>

      <section style={{ background: "var(--ivory)" }}>
        <div className="container">
          <div className="contact-grid">
            {/* Info card */}
            <aside className="contact-info">
              <div className="eyebrow" style={{ color: "var(--gold-soft)" }}>{t("brand.short")}</div>
                   <img src="/logo.png" alt="logo"  style={{ maxWidth: "50px" }} />
              <h2>{t("brand.full")}</h2>
              <p>{t("contact.subtitle")}</p>
    
              <div className="contact-info__item">
                <div className="contact-info__icon">
                  <i className="bi bi-geo-alt" />
                </div>
                <div>
                  <div className="contact-info__label">{t("contact.address_label")}</div>
                  <div className="contact-info__value">{t("contact.address")}</div>
                </div>
              </div>

              <div className="contact-info__item">
                <div className="contact-info__icon">
                  <i className="bi bi-envelope" />
                </div>
                <div>
                  <div className="contact-info__label">{t("contact.email_label")}</div>
                  <div className="contact-info__value">
                    <a href={`mailto:${t("contact.email_site")}`} style={{ color: "var(--ivory)" }}>{t("contact.email_site")}</a>
                  </div>
                </div>
              </div>

              <div className="contact-info__item">
                <div className="contact-info__icon">
                  <i className="bi bi-telephone" />
                </div>
                <div>
                  <div className="contact-info__label">{t("contact.phone_label")}</div>
                  <div className="contact-info__value"><bdi>{t("contact.phone")}</bdi></div>
                </div>
              </div>
            </aside>

            {/* Form */}
            <div className="contact-form">
              <div className="eyebrow">{t("contact.form_title")}</div>
              <h2 style={{ fontSize: 32, marginBottom: 32 }}>{t("contact.form_title")}</h2>

              <form onSubmit={submit}>
                {status.success && <div className="form-alert success">{status.success}</div>}
                {status.error && <div className="form-alert error">{status.error}</div>}

                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="name">{t("contact.name")}</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={change}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="email">{t("contact.email")}</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={change}
                    />
                  </div>
                </div>

                <div className="form-field" style={{ marginBottom: 20 }}>
                  <label htmlFor="subject">{t("contact.subject")}</label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={form.subject}
                    onChange={change}
                  />
                </div>

                <div className="form-field" style={{ marginBottom: 28 }}>
                  <label htmlFor="message">{t("contact.message")}</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    required
                    value={form.message}
                    onChange={change}
                  />
                </div>

                <button type="submit" className="btn btn-primary" disabled={status.loading}>
                  {status.loading ? (
                    <>
                      <i className="bi bi-arrow-repeat" style={{ animation: "spin 1s linear infinite" }} />
                      {t("contact.sending")}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send" />
                      {t("contact.send")}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          <div className="contact-map">
            <iframe
              title="Palais Consulaire — DAJC"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d25571.692881772844!2d3.0469161660452158!3d36.75949215664762!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x128fb2fded7ade59%3A0xdf928cfb502e65fb!2sPalais%20consulaire%20d'Alger!5e0!3m2!1sfr!2sdz!4v1713857504765!5m2!1sfr!2sdz"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </>
  );
}